import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Facebook, Twitter, Instagram, Phone, MapPin } from 'lucide-react';

const Footer = ({ data }) => {
  const logo = data?.candidate?.logo || '';
  const email = data?.socialMedia?.email || '';
  const phone = data?.socialMedia?.phone || '';
  const facebook = data?.socialMedia?.facebook || '#';
  const twitter = data?.socialMedia?.twitter || '#';
  const instagram = data?.socialMedia?.instagram || '#';
  const headquarters = data?.campaignHeadquarters?.fullAddress || '';
  const disclaimer = data?.legal?.disclaimer || '';
  const complianceNote = data?.legal?.complianceNote || '';

  return (
    <footer className="bg-slate-900">
      {/* Main Footer Content - Three Columns */}
      <div className="container mx-auto px-6 lg:px-20 py-16">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          
          {/* Left Column - Logo */}
          <div className="flex items-center justify-center md:justify-start">
            <img 
              src={logo} 
              alt="Clianda Florence Campaign Logo"
              className="h-32 w-auto"
            />
          </div>

          {/* Middle Column - Contact Info */}
          <div>
            <h3 className="text-xl font-extrabold mb-6 text-white uppercase tracking-wide">
              Get In Touch
            </h3>
            <div className="space-y-4">
              <a 
                href={`mailto:${email}`}
                className="flex items-center gap-3 text-white hover:text-purple-300 transition-colors group"
              >
                <Mail className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm whitespace-nowrap">{email}</span>
              </a>
              
              <a 
                href={`tel:${phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-3 text-white hover:text-purple-300 transition-colors group"
              >
                <Phone className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{phone}</span>
              </a>
              
              <div className="flex items-start gap-3 text-white">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{headquarters}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Social Media */}
          <div>
            <h3 className="text-xl font-extrabold mb-6 text-white uppercase tracking-wide">
              Follow the Campaign
            </h3>
            <div className="flex gap-4">
              <a 
                href={facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 hover:bg-purple-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a 
                href={twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 hover:bg-purple-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5 text-white" />
              </a>
              <a 
                href={instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 hover:bg-purple-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer - Separated with Divider */}
      <div className="border-t border-white/10 py-8">
        <div className="container mx-auto px-6 lg:px-20">
          <div className="space-y-6 text-center">
            {/* Legal Links */}
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="footer-legal-links">
              <Link
                to="/privacy-policy/"
                className="text-sm text-slate-300 hover:text-white underline underline-offset-4 transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <span className="text-slate-600">|</span>
              <Link
                to="/terms-and-conditions/"
                className="text-sm text-slate-300 hover:text-white underline underline-offset-4 transition-colors"
                data-testid="footer-terms-link"
              >
                Terms &amp; Conditions
              </Link>
            </div>
            <p className="text-base text-white font-bold">
              {disclaimer}
            </p>
            <p className="text-xs text-slate-400 leading-relaxed max-w-4xl mx-auto">
              {complianceNote}
            </p>
            <p className="text-xs text-slate-500 pt-4">
              © {new Date().getFullYear()} Friends of Clianda Florence. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };
